import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Screen from '../../components/common/Screen';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Icon from '../../components/common/Icon';
import ReCaptchaWidget from '../../components/forms/ReCaptchaWidget';
import { handleBranding } from '../../utils';
import '../readiness/PublicReadiness.css';

/* Public landing page for the free-account activation link (/activate/:token).

   The mail links HERE, to a page, and the page POSTs — never a mutating GET. SendGrid rewrites the
   href for click tracking, so a prefetching link scanner will fetch this URL; it cannot fire the
   POST. (A JavaScript-executing scanner still can, which is why the backend is idempotent.)

   The token is a PATH segment, so usePersistUrlParams — which only strips location.search — leaves
   it alone, exactly like /join/:token and /refer/:token. */

const Activate = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();

  const [state, setState] = useState('working');    // working | expired | failed
  const [stale, setStale] = useState(null);         // the recovered payload behind an expired link
  const [captchaToken, setCaptchaToken] = useState('');
  const [siteKey, setSiteKey] = useState(null);
  const [resent, setResent] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    handleBranding();
    (async () => {
      try {
        const resp = await fetch('/api/public-account/activate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token }),
        });
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        if (resp.ok) {
          // The JWT arrives as an HttpOnly cookie; this flag is what the SPA reads. Without
          // both lines the app still believes nobody is signed in.
          localStorage.setItem('isLoggedIn', '1');
          window.dispatchEvent(new Event('auth-changed'));
          navigate(data.redirect || '/member-home');
          return;
        }
        if (data && data.expired) {
          setStale(data);
          setState('expired');
          if (data.slug) {
            // Fetch the captcha key for the resend, and re-pin the brand.
            localStorage.setItem('brandSlug', data.slug);
            fetch(`/api/public-account/${data.slug}`)
              .then((r) => (r.ok ? r.json() : null))
              .then((cfg) => { if (!cancelled && cfg) setSiteKey(cfg.recaptcha_site_key || null); })
              .catch(() => {});
          }
          return;
        }
        setState('failed');
      } catch (err) {
        if (!cancelled) setState('failed');
      }
    })();
    return () => { cancelled = true; };
  }, [token, navigate]);

  // Ask for a fresh link. Goes through the SAME non-enumerating submit endpoint, with the fields
  // recovered from the (still signature-valid) expired token, so nothing has to be retyped.
  const resend = async () => {
    if (!stale || !stale.slug) return;
    if (siteKey && !captchaToken) { setError(t('completeCaptcha')); return; }
    setResending(true);
    setError(null);
    try {
      const cfg = await fetch(`/api/public-account/${stale.slug}`).then((r) => r.json());
      const resp = await fetch(`/api/public-account/${stale.slug}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          submit_token: cfg.submit_token,
          first_name: stale.first_name || '',
          last_name: stale.last_name || '',
          email: stale.email || '',
          company: stale.company || '',
          phone: stale.phone || '',
          recaptcha_token: captchaToken,
        }),
      });
      if (resp.ok) { setResent(true); return; }
      setCaptchaToken('');                     // single-use token
      setError(t('freeAccountError'));
    } catch (err) {
      setCaptchaToken('');
      setError(t('freeAccountError'));
    } finally {
      setResending(false);
    }
  };

  return (
    <Screen><div className="top">
      <Card>
        <div className="readiness text-center py-3">
          {state === 'working' && (
            <>
              <span className="auth__spinner" />
              <p className="text-muted mt-3">{t('activateWorking')}</p>
            </>
          )}

          {state === 'failed' && (
            <>
              <Title>{t('activateFailedTitle')}</Title>
              <p className="text-muted mx-2">{t('activateFailed')}</p>
            </>
          )}

          {state === 'expired' && (resent ? (
            <>
              <Title>{t('freeAccountSentTitle')}</Title>
              <p className="text-muted mx-2">
                {t('freeAccountSentBody', { email: (stale && stale.email) || '' })}
              </p>
            </>
          ) : (
            <>
              <Title>{t('activateExpiredTitle')}</Title>
              <p className="text-muted mx-2">{t('activateExpired')}</p>
              {siteKey && (
                <div className="mb-3 d-flex justify-content-center">
                  <ReCaptchaWidget
                    siteKey={siteKey}
                    onVerify={(tok) => setCaptchaToken(tok)}
                    onExpire={() => setCaptchaToken('')}
                    onError={() => setCaptchaToken('')}
                  />
                </div>
              )}
              {error && <div className="alert alert-danger" role="alert">{error}</div>}
              <button type="button" className="bravos-submit" onClick={resend} disabled={resending}>
                {resending ? <span className="auth__spinner" /> : <Icon name="envelope" />}
                {t('activateResend')}
              </button>
            </>
          ))}
        </div>
      </Card>
    </div></Screen>
  );
};

export default Activate;
